import {
  AdditionalProperties,
  EmptyAdditionalProperties,
  ValidAdditionalProperties,
} from 'ApplicationEntry/models/AdditionalProperties';
import {AgentInformation, EmptyAgent, ValidAgent} from 'ApplicationEntry/models/AgentInformation';
import {AgentList, EmptyAgentList, ValidAgentList} from 'ApplicationEntry/models/AgentList';
import {
  EmptyOrdinaryAWDStatus,
  OrdinaryAWDStatus,
  ValidOrdinaryAWDStatus,
} from 'ApplicationEntry/models/AWDStatus/OrdinaryAWDStatus';
import {BillingModes, EmptyBillingModes, ValidBillingModes} from 'ApplicationEntry/models/BillingModes';
import {
  ContingentBeneficiary,
  EmptyContingentBeneficiary,
  ValidContingentBeneficiary,
} from 'ApplicationEntry/models/ContingentBeneficiary';
import {
  ContingentBeneficiaryList,
  EmptyContingentBeneficiaryList,
  ValidContingentBeneficiaryList,
} from 'ApplicationEntry/models/ContingentBeneficiaryList';
import {DependentLists, EmptyDependentList, ValidDependentList} from 'ApplicationEntry/models/DependentLists';
import {Dependents, EmptyDependents, ValidDependents} from 'ApplicationEntry/models/Dependents';
import {EmptyHippaTrustees, HippaTrustees, ValidHippaTrustees} from 'ApplicationEntry/models/HippaTrustees';
import {EmptyMessage, Messages, ValidMessage} from 'ApplicationEntry/models/Messages';
import {
  EmptyOrdinaryApplicantDemographics,
  OrdinaryApplicantDemographics,
  ValidOrdinaryApplicantDemographics,
} from 'ApplicationEntry/models/OrdinaryApplicantDemographics';
import {
  EmptyOrdinaryBillingAgent,
  OrdinaryBillingAgent,
  ValidOrdinaryBillingAgent,
} from 'ApplicationEntry/models/OrdinaryBillingAgent';
import {
  EmptyOrdinaryCaseDetails,
  OrdinaryCaseDetails,
  ValidOrdinaryCaseDetails,
} from 'ApplicationEntry/models/OrdinaryCaseDetails';
import {
  EmptyOrdinaryVerifyCriticalInformation,
  OrdinaryVerifyCriticalInformation,
  ValidOrdinaryVerifyCriticalInformation,
} from 'ApplicationEntry/models/OrdinaryVerifyCriticalInformation';
import {EmptyOwnerInfo, OwnerInfo, ValidOwnerInfo} from 'ApplicationEntry/models/OwnerInfo';
import {EmptyPlanCode, PlanCode, ValidPlanCode} from 'ApplicationEntry/models/PlanCode';
import {EmptyPlanCodes, PlanCodes, ValidPlanCodes} from 'ApplicationEntry/models/PlanCodes';
import {EmptyPolicyRecord, PolicyRecord, ValidPolicyRecord} from 'ApplicationEntry/models/PolicyRecord';
import {EmptyPremium, Premium, ValidPremium} from 'ApplicationEntry/models/Premium';
import {
  EmptyPrimaryBeneficiary,
  PrimaryBeneficiary,
  ValidPrimaryBeneficiary,
} from 'ApplicationEntry/models/PrimaryBeneficiary';
import {
  EmptyPrimaryBeneficiaryList,
  PrimaryBeneficiaryList,
  ValidPrimaryBeneficiaryList,
} from 'ApplicationEntry/models/PrimaryBeneficiaryList';
import {
  EmptyOrdinaryAccident,
  OrdinaryAccident,
  ValidOrdinaryAccident,
} from 'ApplicationEntry/models/ProductsRiders/Accident/OrdinaryAccident';
import {
  EmptyOrdinaryAccidentNewYork,
  OrdinaryAccidentNewYork,
  ValidOrdinaryAccidentNewYork,
} from 'ApplicationEntry/models/ProductsRiders/AccidentNewYork/OrdinaryAccidentNewYork';
import {EmptyOrdinaryAIP, OrdinaryAIP, ValidOrdinaryAIP} from 'ApplicationEntry/models/ProductsRiders/AIP/OrdinaryAIP';
import {
  EmptyOrdinaryCancer,
  OrdinaryCancer,
  ValidOrdinaryCancer,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/OrdinaryCancer';
import {
  EmptyOrdCriticalIllness,
  OrdinaryCriticalIllness,
  ValidOrdinaryCriticalIllness,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/OrdinaryCriticalIllness';
import {
  EmptyOrdinaryCriticalIllnessNY,
  OrdinaryCriticalIllnessNY,
  ValidOrdinaryCriticalIllnessNY,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllnessNY/OrdinaryCriticalIllnessNY';
import {
  EmptyOrdDisability,
  OrdDisability,
  ValidOrdDisability,
} from 'ApplicationEntry/models/ProductsRiders/Disability/OrdinaryDisability';
import {EmptyOrdGLTERM, OrdGLTERM} from 'ApplicationEntry/models/ProductsRiders/GLTERM/OrdinaryGLTERM';
import {
  EmptyOrdinaryHeartStroke,
  OrdinaryHeartStroke,
  ValidOrdinaryHeartStroke,
} from 'ApplicationEntry/models/ProductsRiders/HeartStroke/OrdinaryHeartStroke';
import {
  EmptyOrdinaryICRTN,
  OrdinaryICRTN,
  ValidOrdinaryICRTN,
} from 'ApplicationEntry/models/ProductsRiders/ICRTN/OrdinaryICRTN';
import {EmptyOrdinaryIwl, OrdinaryIwl, ValidOrdinaryIwl} from 'ApplicationEntry/models/ProductsRiders/IWL/OrdinaryIWL';
import {EmptyOrdShop, OrdShop, ValidOrdShop} from 'ApplicationEntry/models/ProductsRiders/Shop/OrdinaryShop';
import {EmptyOrdUL, OrdUL, ValidOrdUL} from 'ApplicationEntry/models/ProductsRiders/UniversalLife/OrdinaryUL';
import {
  EmptySecondaryAddressee,
  SecondaryAddressee,
  ValidSecondaryAddressee,
} from 'ApplicationEntry/models/SecondaryAddressee';
import {EmptyUWQuestions, UWQuestions, ValidUWQuestions} from 'ApplicationEntry/models/UnderwritingQuestions';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {ValidNewYorkGLTERM} from './ProductsRiders/GLTERM/NewYorkGLTERM';
import {EmptyOrdinaryPinCare, OrdinaryPinCare, ValidOrdinaryPinCare} from './ProductsRiders/PinCare/OrdinaryPinCare';

type OrdinaryProps = {
  caseDetails: OrdinaryCaseDetails;
  applicantDemographics: OrdinaryApplicantDemographics;
  ownerInfo: OwnerInfo;
  secondaryAddressee: SecondaryAddressee;
  primaryBeneficiaryList: PrimaryBeneficiaryList;
  primaryBeneficiary: PrimaryBeneficiary;
  contingentBeneficiary: ContingentBeneficiary;
  contingentBeneficiaryList: ContingentBeneficiaryList;
  criticalIllness: OrdinaryCriticalIllness;
  icrtn: OrdinaryICRTN;
  cancer: OrdinaryCancer;
  dependents: Dependents;
  dependentLists: DependentLists;
  policyRecord: PolicyRecord;
  billingAgent: OrdinaryBillingAgent;
  agentInformation: AgentInformation;
  agentList: AgentList;
  messages: Messages;
  billingModes: BillingModes;
  awdStatus: OrdinaryAWDStatus;
  verifyCriticalInformation: OrdinaryVerifyCriticalInformation;
  disability: OrdDisability;
  shop: OrdShop;
  iwl: OrdinaryIwl;
  heartStroke: OrdinaryHeartStroke;
  accident: OrdinaryAccident;
  uwQuestions: UWQuestions;
  universalLife: OrdUL;
  glterm: OrdGLTERM;
  pinCare: OrdinaryPinCare;
  accidentNewYork: OrdinaryAccidentNewYork;
  criticalIllnessNY: OrdinaryCriticalIllnessNY;
  hippaTrustees: HippaTrustees;
  premium: Premium;
  aip: OrdinaryAIP;
  planCode: PlanCode;
  planCodes: PlanCodes;
  additionalProperties: AdditionalProperties;
};

export class InvalidOrdinaryApplication {
  public readonly key = 'InvalidOrdinaryApplication';

  constructor(
    public readonly caseDetails: OrdinaryCaseDetails,
    public readonly applicantDemographics: OrdinaryApplicantDemographics,
    public readonly ownerInfo: OwnerInfo,
    public readonly secondaryAddressee: SecondaryAddressee,
    public readonly primaryBeneficiaryList: PrimaryBeneficiaryList,
    public readonly primaryBeneficiary: PrimaryBeneficiary,
    public readonly contingentBeneficiary: ContingentBeneficiary,
    public readonly contingentBeneficiaryList: ContingentBeneficiaryList,
    public readonly criticalIllness: OrdinaryCriticalIllness,
    public readonly icrtn: OrdinaryICRTN,
    public readonly cancer: OrdinaryCancer,
    public readonly dependents: Dependents,
    public readonly dependentLists: DependentLists,
    public readonly policyRecord: PolicyRecord,
    public readonly billingAgent: OrdinaryBillingAgent,
    public readonly agentInformation: AgentInformation,
    public readonly agentList: AgentList,
    public readonly messages: Messages,
    public readonly billingModes: BillingModes,
    public readonly awdStatus: OrdinaryAWDStatus,
    public readonly verifyCriticalInformation: OrdinaryVerifyCriticalInformation,
    public readonly disability: OrdDisability,
    public readonly shop: OrdShop,
    public readonly iwl: OrdinaryIwl,
    public readonly heartStroke: OrdinaryHeartStroke,
    public readonly accident: OrdinaryAccident,
    public readonly uwQuestions: UWQuestions,
    public readonly universalLife: OrdUL,
    public readonly glterm: OrdGLTERM,
    public readonly pinCare: OrdinaryPinCare,
    public readonly accidentNewYork: OrdinaryAccidentNewYork,
    public readonly criticalIllnessNY: OrdinaryCriticalIllnessNY,
    public readonly hippaTrustees: HippaTrustees,
    public readonly premium: Premium,
    public readonly aip: OrdinaryAIP,
    public readonly planCode: PlanCode,
    public readonly planCodes: PlanCodes,
    public readonly additionalProperties: AdditionalProperties
  ) {}
}

export class ValidOrdinaryApplication {
  public static createApp({
    caseDetails,
    applicantDemographics,
    ownerInfo,
    secondaryAddressee,
    primaryBeneficiaryList,
    primaryBeneficiary,
    contingentBeneficiary,
    contingentBeneficiaryList,
    criticalIllness,
    icrtn,
    cancer,
    dependents,
    dependentLists,
    policyRecord,
    billingAgent,
    agentInformation,
    agentList,
    messages,
    billingModes,
    awdStatus,
    verifyCriticalInformation,
    disability,
    shop,
    iwl,
    heartStroke,
    accident,
    uwQuestions,
    universalLife,
    glterm,
    pinCare,
    accidentNewYork,
    criticalIllnessNY,
    hippaTrustees,
    premium,
    aip,
    planCode,
    planCodes,
    additionalProperties,
  }: OrdinaryProps) {
    return this.create(
      caseDetails,
      applicantDemographics,
      ownerInfo,
      secondaryAddressee,
      primaryBeneficiaryList,
      primaryBeneficiary,
      contingentBeneficiary,
      contingentBeneficiaryList,
      criticalIllness,
      icrtn,
      cancer,
      dependents,
      dependentLists,
      policyRecord,
      billingAgent,
      agentInformation,
      agentList,
      messages,
      billingModes,
      awdStatus,
      verifyCriticalInformation,
      disability,
      shop,
      iwl,
      heartStroke,
      accident,
      uwQuestions,
      universalLife,
      glterm,
      pinCare,
      accidentNewYork,
      criticalIllnessNY,
      hippaTrustees,
      premium,
      aip,
      planCode,
      planCodes,
      additionalProperties
    );
  }

  public static create(
    caseDetails: OrdinaryCaseDetails,
    applicantDemographics: OrdinaryApplicantDemographics,
    ownerInfo: OwnerInfo,
    secondaryAddressee: SecondaryAddressee,
    primaryBeneficiaryList: PrimaryBeneficiaryList,
    primaryBeneficiary: PrimaryBeneficiary,
    contingentBeneficiary: ContingentBeneficiary,
    contingentBeneficiaryList: ContingentBeneficiaryList,
    criticalIllness: OrdinaryCriticalIllness,
    icrtn: OrdinaryICRTN,
    cancer: OrdinaryCancer,
    dependents: Dependents,
    dependentLists: DependentLists,
    policyRecord: PolicyRecord,
    billingAgent: OrdinaryBillingAgent,
    agentInformation: AgentInformation,
    agentList: AgentList,
    messages: Messages,
    billingModes: BillingModes,
    awdStatus: OrdinaryAWDStatus,
    verifyCriticalInformation: OrdinaryVerifyCriticalInformation,
    disability: OrdDisability,
    shop: OrdShop,
    iwl: OrdinaryIwl,
    heartStroke: OrdinaryHeartStroke,
    accident: OrdinaryAccident,
    uwQuestions: UWQuestions,
    universalLife: OrdUL,
    glterm: OrdGLTERM,
    pinCare: OrdinaryPinCare,
    accidentNewYork: OrdinaryAccidentNewYork,
    criticalIllnessNY: OrdinaryCriticalIllnessNY,
    hippaTrustees: HippaTrustees,
    premium: Premium,
    aip: OrdinaryAIP,
    planCode: PlanCode,
    planCodes: PlanCodes,
    additionalProperties: AdditionalProperties
  ) {
    return caseDetails instanceof ValidOrdinaryCaseDetails &&
      applicantDemographics instanceof ValidOrdinaryApplicantDemographics &&
      ownerInfo instanceof ValidOwnerInfo &&
      secondaryAddressee instanceof ValidSecondaryAddressee &&
      primaryBeneficiaryList instanceof ValidPrimaryBeneficiaryList &&
      primaryBeneficiary instanceof ValidPrimaryBeneficiary &&
      contingentBeneficiary instanceof ValidContingentBeneficiary &&
      contingentBeneficiaryList instanceof ValidContingentBeneficiaryList &&
      criticalIllness instanceof ValidOrdinaryCriticalIllness &&
      icrtn instanceof ValidOrdinaryICRTN &&
      cancer instanceof ValidOrdinaryCancer &&
      dependents instanceof ValidDependents &&
      dependentLists instanceof ValidDependentList &&
      policyRecord instanceof ValidPolicyRecord &&
      billingAgent instanceof ValidOrdinaryBillingAgent &&
      agentInformation instanceof ValidAgent &&
      agentList instanceof ValidAgentList &&
      messages instanceof ValidMessage &&
      billingModes instanceof ValidBillingModes &&
      awdStatus instanceof ValidOrdinaryAWDStatus &&
      verifyCriticalInformation instanceof ValidOrdinaryVerifyCriticalInformation &&
      disability instanceof ValidOrdDisability &&
      shop instanceof ValidOrdShop &&
      iwl instanceof ValidOrdinaryIwl &&
      heartStroke instanceof ValidOrdinaryHeartStroke &&
      accident instanceof ValidOrdinaryAccident &&
      uwQuestions instanceof ValidUWQuestions &&
      universalLife instanceof ValidOrdUL &&
      glterm instanceof ValidNewYorkGLTERM &&
      pinCare instanceof ValidOrdinaryPinCare &&
      accidentNewYork instanceof ValidOrdinaryAccidentNewYork &&
      criticalIllnessNY instanceof ValidOrdinaryCriticalIllnessNY &&
      hippaTrustees instanceof ValidHippaTrustees &&
      premium instanceof ValidPremium &&
      aip instanceof ValidOrdinaryAIP &&
      planCode instanceof ValidPlanCode &&
      planCodes instanceof ValidPlanCodes &&
      additionalProperties instanceof ValidAdditionalProperties
      ? new ValidOrdinaryApplication(
          caseDetails,
          applicantDemographics,
          ownerInfo,
          secondaryAddressee,
          primaryBeneficiaryList,
          primaryBeneficiary,
          contingentBeneficiary,
          contingentBeneficiaryList,
          criticalIllness,
          icrtn,
          cancer,
          dependents,
          dependentLists,
          policyRecord,
          billingAgent,
          agentInformation,
          agentList,
          messages,
          billingModes,
          awdStatus,
          verifyCriticalInformation,
          disability,
          shop,
          iwl,
          heartStroke,
          accident,
          uwQuestions,
          universalLife,
          glterm,
          pinCare,
          accidentNewYork,
          criticalIllnessNY,
          hippaTrustees,
          premium,
          aip,
          planCode,
          planCodes,
          additionalProperties
        )
      : new InvalidOrdinaryApplication(
          caseDetails,
          applicantDemographics,
          ownerInfo,
          secondaryAddressee,
          primaryBeneficiaryList,
          primaryBeneficiary,
          contingentBeneficiary,
          contingentBeneficiaryList,
          criticalIllness,
          icrtn,
          cancer,
          dependents,
          dependentLists,
          policyRecord,
          billingAgent,
          agentInformation,
          agentList,
          messages,
          billingModes,
          awdStatus,
          verifyCriticalInformation,
          disability,
          shop,
          iwl,
          heartStroke,
          accident,
          uwQuestions,
          universalLife,
          glterm,
          pinCare,
          accidentNewYork,
          criticalIllnessNY,
          hippaTrustees,
          premium,
          aip,
          planCode,
          planCodes,
          additionalProperties
        );
  }

  public readonly key = 'ValidOrdinaryApplication';

  private constructor(
    public readonly caseDetails: ValidOrdinaryCaseDetails,
    public readonly applicantDemographics: ValidOrdinaryApplicantDemographics,
    public readonly ownerInfo: ValidOwnerInfo,
    public readonly secondaryAddressee: SecondaryAddressee,
    public readonly primaryBeneficiaryList: ValidPrimaryBeneficiaryList,
    public readonly primaryBeneficiary: ValidPrimaryBeneficiary,
    public readonly contingentBeneficiary: ValidContingentBeneficiary,
    public readonly contingentBeneficiaryList: ValidContingentBeneficiaryList,
    public readonly criticalIllness: OrdinaryCriticalIllness,
    public readonly icrtn: ValidOrdinaryICRTN,
    public readonly cancer: ValidOrdinaryCancer,
    public readonly dependents: Dependents,
    public readonly dependentLists: DependentLists,
    public readonly policyRecord: PolicyRecord,
    public readonly billingAgent: ValidOrdinaryBillingAgent,
    public readonly agentInformation: ValidAgent,
    public readonly agentList: ValidAgentList,
    public readonly messages: ValidMessage,
    public readonly billingModes: ValidBillingModes,
    public readonly awdStatus: ValidOrdinaryAWDStatus,
    public readonly verifyCriticalInformation: ValidOrdinaryVerifyCriticalInformation,
    public readonly disability: ValidOrdDisability,
    public readonly shop: ValidOrdShop,
    public readonly iwl: ValidOrdinaryIwl,
    public readonly heartStroke: ValidOrdinaryHeartStroke,
    public readonly accident: ValidOrdinaryAccident,
    public readonly uwQuestions: UWQuestions,
    public readonly universalLife: OrdUL,
    public readonly glterm: OrdGLTERM,
    public readonly pinCare: ValidOrdinaryPinCare,
    public readonly accidentNewYork: ValidOrdinaryAccidentNewYork,
    public readonly criticalIllnessNY: ValidOrdinaryCriticalIllnessNY,
    public readonly hippaTrustees: ValidHippaTrustees,
    public readonly premium: Premium,
    public readonly aip: ValidOrdinaryAIP,
    public readonly planCode: PlanCode,
    public readonly planCodes: PlanCodes,
    public readonly additionalProperties: AdditionalProperties
  ) {}
}

export type OrdinaryApplication = ValidOrdinaryApplication | InvalidOrdinaryApplication;

export type OrdinaryApplicationOptions<TReturn> = ToMatchOptions<OrdinaryApplication, 'key', TReturn>;

export const matchOrdinaryApplication = <TReturn>(
  ordinaryApplication: OrdinaryApplication,
  options: OrdinaryApplicationOptions<TReturn>
): TReturn => {
  switch (ordinaryApplication.key) {
    case 'InvalidOrdinaryApplication':
      return options.InvalidOrdinaryApplication(ordinaryApplication);
    case 'ValidOrdinaryApplication':
      return options.ValidOrdinaryApplication(ordinaryApplication);
  }
};

export const changeStandardCaseDetails = (caseDetails: OrdinaryCaseDetails) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, caseDetails});

export const changeApplicantDemographics = (applicantDemographics: OrdinaryApplicantDemographics) => (
  app: OrdinaryApplication
) => ValidOrdinaryApplication.createApp({...app, applicantDemographics});

export const changeSecondaryAddressee = (secondaryAddressee: SecondaryAddressee) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, secondaryAddressee});

export const changePrimaryBeneficiaryList = (primaryBeneficiaryList: PrimaryBeneficiaryList) => (
  app: OrdinaryApplication
) => ValidOrdinaryApplication.createApp({...app, primaryBeneficiaryList});

export const changePrimaryBeneficiary = (primaryBeneficiary: PrimaryBeneficiary) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, primaryBeneficiary});

export const changeContingentBeneficiary = (contingentBeneficiary: ContingentBeneficiary) => (
  app: OrdinaryApplication
) => ValidOrdinaryApplication.createApp({...app, contingentBeneficiary});

export const changeContingentBeneficiaryList = (contingentBeneficiaryList: ContingentBeneficiaryList) => (
  app: OrdinaryApplication
) => ValidOrdinaryApplication.createApp({...app, contingentBeneficiaryList});

export const changeDependents = (dependents: Dependents) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, dependents});

export const changeDependentLists = (dependentLists: DependentLists) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, dependentLists});

export const changePolicyRecord = (policyRecord: PolicyRecord) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, policyRecord});

export const changeBillingAgent = (billingAgent: OrdinaryBillingAgent) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, billingAgent});

export const changeAgent = (agentInformation: AgentInformation) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, agentInformation});

export const changeAgentList = (agentList: AgentList) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, agentList});

export const changeMessages = (messages: Messages) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, messages});

export const changeBillingModes = (billingModes: BillingModes) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, billingModes});

export const changeAWDStatus = (awdStatus: OrdinaryAWDStatus) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, awdStatus});

export const changeVerifyCriticalInformation = (verifyCriticalInformation: OrdinaryVerifyCriticalInformation) => (
  app: OrdinaryApplication
) => ValidOrdinaryApplication.createApp({...app, verifyCriticalInformation});

export const changeDisability = (disability: OrdDisability) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, disability});

export const changeShop = (shop: OrdShop) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, shop});

export const changeIWL = (iwl: OrdinaryIwl) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, iwl});

export const changeCancer = (cancer: OrdinaryCancer) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, cancer});

export const changeICRTN = (icrtn: OrdinaryICRTN) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, icrtn});

export const changeHeartStroke = (heartStroke: OrdinaryHeartStroke) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, heartStroke});

export const changeAccident = (accident: OrdinaryAccident) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, accident});

export const changeUWQuestions = (uwQuestions: UWQuestions) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, uwQuestions});

export const changeUniversalLife = (universalLife: OrdUL) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, universalLife});

export const changeGLTERM = (glterm: OrdGLTERM) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, glterm});

export const changePinCare = (pinCare: OrdinaryPinCare) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, pinCare});

export const changeAccidentNewYork = (accidentNewYork: OrdinaryAccidentNewYork) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, accidentNewYork});

export const changeCriticalIllnessNY = (criticalIllnessNY: OrdinaryCriticalIllnessNY) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, criticalIllnessNY});

export const changeHippaTrustees = (hippaTrustees: HippaTrustees) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, hippaTrustees});

export const changePremium = (premium: Premium) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, premium});
export const changeAIP = (aip: OrdinaryAIP) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, aip});

export const changePlanCode = (planCode: PlanCode) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, planCode});

export const changePlanCodes = (planCodes: PlanCodes) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, planCodes});

export const changeAdditionalProperties = (additionalProperties: AdditionalProperties) => (app: OrdinaryApplication) =>
  ValidOrdinaryApplication.createApp({...app, additionalProperties});

export const EmptyOrdinaryApplication = new InvalidOrdinaryApplication(
  EmptyOrdinaryCaseDetails,
  EmptyOrdinaryApplicantDemographics,
  EmptyOwnerInfo,
  EmptySecondaryAddressee,
  EmptyPrimaryBeneficiaryList,
  EmptyPrimaryBeneficiary,
  EmptyContingentBeneficiary,
  EmptyContingentBeneficiaryList,
  EmptyOrdCriticalIllness,
  EmptyOrdinaryICRTN,
  EmptyOrdinaryCancer,
  EmptyDependents,
  EmptyDependentList,
  EmptyPolicyRecord,
  EmptyOrdinaryBillingAgent,
  EmptyAgent,
  EmptyAgentList,
  EmptyMessage,
  EmptyBillingModes,
  EmptyOrdinaryAWDStatus,
  EmptyOrdinaryVerifyCriticalInformation,
  EmptyOrdDisability,
  EmptyOrdShop,
  EmptyOrdinaryIwl,
  EmptyOrdinaryHeartStroke,
  EmptyOrdinaryAccident,
  EmptyUWQuestions,
  EmptyOrdUL,
  EmptyOrdGLTERM,
  EmptyOrdinaryPinCare,
  EmptyOrdinaryAccidentNewYork,
  EmptyOrdinaryCriticalIllnessNY,
  EmptyHippaTrustees,
  EmptyPremium,
  EmptyOrdinaryAIP,
  EmptyPlanCode,
  EmptyPlanCodes,
  EmptyAdditionalProperties
);
